import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HandHeart, Hand, Sparkles, ArrowRight } from "lucide-react";
import { StoryVideo } from "@/components/StoryVideo";
import { FollowJourneyPill } from "@/components/FollowJourneyPill";

export const Mission = ({ showLearnMore = true, showVideo = true, showSocial = true }) => {
  return (
    <section id="mission" data-testid="mission-section" className="py-14 sm:py-20 md:py-32 px-5 md:px-10 bg-[#ff648b]">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#111111] tracking-tight flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
          <motion.img
            src="/brand-donut-choco.png?v=2"
            alt=""
            aria-hidden="true"
            animate={{ rotate: [0, -8, 8, -6, 6, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 2, ease: "easeInOut" }}
            className="h-[66px] sm:h-[84px] w-auto object-contain shrink-0 select-none drop-shadow-[0_6px_10px_rgba(62,39,35,0.2)]"
          />
          Sweetness with a purpose
        </h2>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-lg text-[#111111]/85 mt-7 leading-relaxed max-w-3xl mx-auto"
        >
          Bobo's Donuts™ is a family-run company on a mission to help families of
          deaf and hard of hearing (DHH) children with intellectual disabilities find the support
          they need. Our goal is to become a fully inclusive community that
          embraces everyone's strengths and gifts — and to teach our neighbors ASL
          through our videos and everyday interactions.
        </motion.p>

        {showVideo && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12 max-w-3xl mx-auto"
          >
            <StoryVideo />
          </motion.div>
        )}

        <div className="grid sm:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-12 text-left">
          {[
            { icon: HandHeart, title: "Family & inclusion", text: "Embracing and valuing the strengths every person brings to the table." },
            { icon: Hand, title: "Teaching ASL", text: "Sharing American Sign Language online and in our community, one window at a time." },
            { icon: Sparkles, title: "Building futures", text: "Creating meaningful work for adults with differing abilities." },
          ].map((c) => (
            <div
              key={c.title}
              className="flex gap-4 items-start sm:block sm:bg-[#f7f0eb] sm:rounded-3xl sm:p-7 sm:shadow-lg"
            >
              <div className="shrink-0 w-12 h-12 rounded-2xl bg-[#ff648b] flex items-center justify-center sm:mb-4">
                <c.icon className="h-6 w-6 text-[#111111]" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-xl text-[#111111]">{c.title}</h3>
                <p className="text-[#3d3d3d] mt-1 sm:mt-2">{c.text}</p>
              </div>
            </div>
          ))}
        </div>

        {(showLearnMore || showSocial) && (
          <div className="flex flex-wrap items-center justify-center gap-4 mt-10 sm:mt-12">
            {showLearnMore && (
              <Link
                to="/about"
                data-testid="mission-learn-more"
                className="inline-flex items-center gap-2 px-7 py-3 sm:py-4 rounded-full bg-[#111111] text-[#f7f0eb] font-bold text-lg hover:bg-[#3d3d3d] hover:scale-105 transition-all shadow-lg"
              >
                Learn More <ArrowRight className="h-5 w-5" />
              </Link>
            )}

            {showSocial && <FollowJourneyPill />}
          </div>
        )}
      </div>
    </section>
  );
};
